// =====================================================================
//  PESOS DEL PUNTAJE — solo máster
//
//  Define cuánto influye cada criterio en el puntaje de 0 a 100. Afecta a
//  las 39 concesiones y al orden del listado para todo el equipo, así que
//  está reservado al máster.
//
//  El puntaje se normaliza por la suma de los pesos, así que funciona
//  aunque no den 100 — pero conviene dejarlos en 100 para que el número
//  se pueda leer como un porcentaje.
// =====================================================================

import { useCriterios, useGuardarPeso, usePuntajes } from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga, Tag, TituloCard, useAvisar } from '../componentes/ui'
import { Pagina } from '../App'

export function Pesos() {
  const criterios = useCriterios()
  const puntajes = usePuntajes()
  const guardar = useGuardarPeso()
  const avisar = useAvisar()

  if (criterios.isError) return <ErrorCarga error={criterios.error} />
  if (criterios.isLoading || !criterios.data) return <Cargando />

  const total = criterios.data.reduce((s, k) => s + k.peso, 0)
  const ps = (puntajes.data ?? []).map((p) => p.puntaje)
  const minimo = ps.length ? Math.min(...ps) : 0
  const maximo = ps.length ? Math.max(...ps) : 0

  const cambiar = (codigo: string, peso: number) =>
    guardar.mutateAsync({ codigo, peso }).catch(avisar)

  const restaurar = async () => {
    for (const k of criterios.data!) {
      const original = k.peso_defecto
      if (original !== undefined && original !== k.peso) {
        await cambiar(k.codigo, original)
      }
    }
  }

  return (
    <>
      <Pagina
        titulo="Pesos del puntaje"
        sub="Cuánto influye cada criterio en el puntaje de 0 a 100. Afecta a todo el equipo y al orden del listado."
      />

      <div className="card" style={{ maxWidth: 780 }}>
        {criterios.data.map((k) => (
          <div key={k.codigo} style={{ padding: '13px 0', borderBottom: '1px solid #1c242f' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14 }}
            >
              <div>
                <b>{k.nombre}</b>{' '}
                <Tag clase={k.automatico ? 't-remate' : 't-media'}>
                  {k.automatico ? 'automático' : 'manual'}
                </Tag>
                <p className="sub" style={{ marginTop: 4, maxWidth: 520 }}>
                  {k.descripcion}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                <input
                  type="range"
                  min={0}
                  max={40}
                  value={k.peso}
                  onChange={(e) => void cambiar(k.codigo, Number(e.target.value))}
                />
                <b style={{ width: 42, textAlign: 'right' }}>{k.peso}%</b>
              </div>
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 14 }}>
          <b>Total</b>
          <b style={{ color: total === 100 ? 'var(--ok)' : 'var(--warn)' }}>{total}%</b>
        </div>

        {total !== 100 ? (
          <Alerta tono="warn" estilo={{ marginTop: 12 }}>
            Los pesos suman {total}%. El puntaje se normaliza igual —se divide por la suma real— pero
            conviene dejarlo en 100 para que el número se lea como un porcentaje.
          </Alerta>
        ) : null}

        <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => void restaurar()}>
            Restaurar valores por defecto
          </button>
          <span className="sub">
            Rango actual del listado: <b>{minimo}</b> a <b>{maximo}</b> puntos
          </span>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, maxWidth: 780, background: 'var(--panel2)' }}>
        <TituloCard>Por qué «escala» mide dos cosas a la vez</TituloCard>
        <p style={{ fontSize: 12.5, color: 'var(--txt2)' }}>
          En estas nóminas el monto adeudado es exactamente las UTM por $71.506: son la{' '}
          <b>misma variable</b>. Un primer diseño usaba «costo de entrada» y «probabilidad de llegar a
          remate» como criterios separados, pero al ser perfectamente anticorrelados se anulaban entre
          sí y todo el listado quedaba comprimido entre 54 y 72 puntos.
        </p>
        <p style={{ fontSize: 12.5, color: 'var(--txt2)', marginTop: 8 }}>
          Se fundieron en uno: a más UTM, más grande el activo <b>y</b> más caro le sale al dueño
          rescatarlo, porque debe pagar el doble (art. 149). El calce presupuestario recoge la parte
          de «es caro» que sí importa, y lo hace con saturación en vez de linealmente, para no volver
          a ser el simple inverso.
        </p>
        <p style={{ fontSize: 12.5, color: 'var(--txt3)', marginTop: 8 }}>
          Los criterios manuales sin evaluar valen 3 —el valor neutro— y su peso igual entra al
          cálculo. Es lo que mantiene los puntajes comparables entre una concesión evaluada y otra
          sin evaluar: si el peso saliera del denominador, no evaluar subiría el puntaje.
        </p>
      </div>
    </>
  )
}
